import { observer } from "mobx-react";
import React from "react";

import DropdownPicker, { type DropdownOption } from "./DropdownPicker";
import { ImgLayoutLandscape } from "@images/ImgLayoutLandscape";
import { ImgLayoutSquare } from "@images/ImgLayoutSquare";
import { ImgLayoutPortrait } from "@images/ImgLayoutPortrait";
import { ImgLayoutWide } from "@images/ImgLayoutWide";
import { ImgLayoutTall } from "@images/ImgLayoutTall";
import { ImgLayoutAsImage } from "@images/ImgLayoutAsImage";

/** Output proportions. 'as-image' follows the first attached image's aspect ratio. */
export type ImageLayout = 'wide' | 'tall' | 'landscape' | 'square' | 'portrait' | 'as-image';

/** Output size options. 'as-image' matches the reference image size (clipped to model limits). */
export type ImageSizeChoice = '1024' | '1536' | '2048' | 'as-image';

/** Video resolution choices (height in pixels). */
export type VideoSizeChoice = '480' | '640' | '720';

type ImageLayoutPickerProps = {
  layout: ImageLayout;
  onSelect: (layout: ImageLayout) => void;
  size: ImageSizeChoice;
  onSizeSelect: (size: ImageSizeChoice) => void;
  imageAttached?: boolean;
  asImageSize?: { width: number; height: number };
  /** When false (guests), the higher resolution tiers (1.5K/2K, 640p/720p) are disabled. */
  canUseHighRes?: boolean;
  /** Video generation mode: video size options, no 'wide'/'tall' layout. FPS/Length are separate controls. */
  video?: {
    size: VideoSizeChoice;
    onSizeSelect: (size: VideoSizeChoice) => void;
  };
};

const LAYOUT_OPTIONS: { value: ImageLayout; ratio: string; label: string; icon: React.ReactNode }[] = [
  { value: 'landscape', ratio: '3:2', label: 'Landscape', icon: <ImgLayoutLandscape size={20} /> },
  { value: 'square', ratio: '1:1', label: 'Square', icon: <ImgLayoutSquare size={20} /> },
  { value: 'portrait', ratio: '2:3', label: 'Portrait', icon: <ImgLayoutPortrait size={20} /> },
  { value: 'wide', ratio: '16:9', label: 'Wide landscape', icon: <ImgLayoutWide size={20} /> },
  { value: 'tall', ratio: '9:16', label: 'Wide portrait', icon: <ImgLayoutTall size={20} /> },
  { value: 'as-image', ratio: '', label: 'As reference image', icon: <ImgLayoutAsImage size={20} /> },
];

const SIZE_OPTIONS: DropdownOption[] = [
  { value: '1024', label: '1K', title: '1024px, clipped to layout' },
  { value: '1536', label: '1.5K', title: '1536px, clipped to layout' },
  { value: '2048', label: '2K', title: '2048px, clipped to layout' },
  { value: 'as-image', label: 'As image', title: 'If possible, otherwise the max for this image generator' },
];

const VIDEO_SIZE_OPTIONS: DropdownOption[] = [
  { value: '480', label: '480p', title: '480p (fastest, lowest VRAM)' },
  { value: '640', label: '640p', title: '640p' },
  { value: '720', label: '720p', title: '720p (highest quality)' },
];

export function getLayoutIcon(layout: ImageLayout, size = 24): React.ReactNode {
  switch (layout) {
    case 'landscape':
      return <ImgLayoutLandscape size={size} />;
    case 'square':
      return <ImgLayoutSquare size={size} />;
    case 'portrait':
      return <ImgLayoutPortrait size={size} />;
    case 'wide':
      return <ImgLayoutWide size={size} />;
    case 'tall':
      return <ImgLayoutTall size={size} />;
    case 'as-image':
      return <ImgLayoutAsImage size={size} />;
    default:
      return null;
  }
}

/**
 * Compact layout (proportions) + size pickers (image mode) or layout + size +
 * FPS + length (video mode). Each control is a single button showing the
 * current choice, backed by the shared DropdownPicker (portal menu, flies up,
 * 500ms hover-leave delay).
 */
@observer
class ImageLayoutPicker extends React.Component<ImageLayoutPickerProps> {
  render() {
    const { layout, onSelect, size, onSizeSelect, imageAttached, asImageSize, video, canUseHighRes = true } = this.props;

    // Video mode: only orientations the video models support (no 'wide'/'tall').
    const layoutOptions = LAYOUT_OPTIONS
      .filter(o => !video || (o.value !== 'wide' && o.value !== 'tall'))
      .map(o => ({
        value: o.value,
        label: o.label,
        icon: o.icon,
        right: o.ratio || undefined,
        title: o.value === 'as-image'
          ? (imageAttached ? 'Follow the reference image aspect ratio' : 'As reference: attach an image first')
          : `${o.label}${o.ratio ? ` (${o.ratio})` : ''}`,
        disabled: o.value === 'as-image' && !imageAttached,
      }));

    const asImageTitle = asImageSize
      ? `If possible, otherwise the max for this image generator (${asImageSize.width}×${asImageSize.height})`
      : 'Attach an image first';
    const imageOptions: DropdownOption[] = SIZE_OPTIONS.map(o => {
      if (o.value !== 'as-image') {
        const highRes = o.value === '1536' || o.value === '2048';
        return highRes && !canUseHighRes ? { ...o, disabled: true, title: `${o.label} — requires an account` } : o;
      }
      return {
        ...o,
        label: imageAttached && asImageSize ? `As image ${asImageSize.width}×${asImageSize.height}` : 'As image',
        title: asImageTitle,
        disabled: !imageAttached,
      };
    });

    const videoOptions: DropdownOption[] = VIDEO_SIZE_OPTIONS.map(o => {
      const highRes = o.value === '640' || o.value === '720';
      return highRes && !canUseHighRes ? { ...o, disabled: true, title: `${o.label} — requires an account` } : o;
    });

    return (
      <div className="control-group image-size-controls">
        <label>Layout:</label>
        <DropdownPicker options={layoutOptions} value={layout} onSelect={v => onSelect(v as ImageLayout)} />
        <label>Size:</label>
        {video
          ? <DropdownPicker options={videoOptions} value={video.size} onSelect={v => video.onSizeSelect(v as VideoSizeChoice)} />
          : <DropdownPicker options={imageOptions} value={size} onSelect={v => onSizeSelect(v as ImageSizeChoice)} />}
      </div>
    );
  }
}

export default ImageLayoutPicker;